// src/templates/public-portal/variants/LogisticPortal.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Service, Company, Product, Bundle, API_BS_URL} from '../../../services/api';
import {
  User, FileText,  Package, Menu, Lock, ArrowRight, ArrowLeft, ArrowUpRight, Layers, Settings, Utensils, Briefcase, PhoneCall, Heart, ChevronDown, ShoppingBag, ChevronLeft, ChevronRight, Play,Plus, Trash2,
  CheckCircle, Info, CreditCard, UtensilsCrossed, ShoppingCart, X,
  Search, Calendar, Minus, Check, User2, Eye,
  Zap,MapPin, ArrowUpDown, Globe, Headphones, Truck,
  Linkedin,
  Instagram,
  Facebook,
  Leaf,
  Target,
  ShieldCheck,
  Star,
  Quote
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../../services/api';

const SERVER_BASE_URL = import.meta.env.VITE_API_BS_URL || 'http://localhost:5000';

// Helper function to convert relative paths to absolute URLs
const getImageUrl = (imagePath: string): string => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http')) return imagePath; // Already absolute
  return `${SERVER_BASE_URL}${imagePath}`; // Convert relative to absolute
};

interface LogisticProps {
  company: Company;
  slug: string;
  services: Service[];
  products?: Product[];
  bundles?: Bundle[];
  portalContent?: any;
}

type CatalogType = 'services' | 'products' | 'bundles';

type CartItem = {
  itemId: string;
  type: CatalogType;
  quantity: number;
  name: string;
  price: number;
  image?: string;
  madeToOrder?: boolean;
  orderPrice?: number;
  deliveryDays?: number;
  wantsOrder?: boolean;
};

interface ClientInfo {
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  taxId: string;
  vatNumber?: string;
  billingAddress: {
    street: string;
    city: string;
    country: string;
  };
}

const LogisticPortal: React.FC<LogisticProps> = ({ 
  company, 
  slug, 
  services: initialServices,
  products: initialProducts,
  bundles: initialBundles,
  portalContent = {
    hero: {
    headline: "Logística & Infraestrutura Global",
    subheadline: "Gestão Integrada de Frotas, Transporte de Cargas e Soluções de Armazenamento de Alta Performance",
    backgroundImage: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=2000"
  },
   about: {
    enabled: true,
    title: "Conectando Mercados com Eficiência e Precisão Cirúrgica",
    body: "<p>Somos líderes em soluções logísticas ponta a ponta. Com uma infraestrutura moderna e tecnologia de rastreamento em tempo real, garantimos a segurança e a agilidade na movimentação das suas cargas em todo o território nacional e internacional.</p>",
    image: "https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&q=80&w=1000"
  },
   missionVision: {
    enabled: true,
    mission: { 
      title: "Nossa Missão", 
      content: "Proporcionar soluções de transporte e logística integradas com máxima eficiência, redução de custos operacionais e pontualidade rigorosa para a sua cadeia de suprimentos." 
    },
    vision: { 
      title: "Nossa Visão", 
      content: "Ser a referência global em logística inteligente e sustentável, impulsionando o comércio por meio de tecnologia avançada de gestão de frota." 
    },
    values: { 
      title: "Nossos Valores", 
      items: [
        "Pontualidade Absoluta", 
        "Segurança & Rastreabilidade", 
        "Transparência Operacional", 
        "Inovação em Supply Chain"
      ] 
    }
  },
    clients: { enabled: true, items: [] },
    testimonials: { enabled: true, items: [] }
  },
}) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeCatalog, setActiveCatalog] = useState<CatalogType>('services');
  const [searchTerm, setSearchTerm] = useState('');
  const [services, setServices] = useState<Service[]>(initialServices || []);

  // will calculate available catalogs once services/products/bundles are defined later

  // track searches performed by visitors (debounced so we don't spam the API)
   // logging de pesquisa já não depende de activeCatalog
  useEffect(() => {
    if (!searchTerm || !slug) return;
   const handler = setTimeout(async () => {
  try {
    await api.public.logSearch(slug, { term: searchTerm, catalog: 'all' });
    console.log(`✅ Pesquisa registrada: "${searchTerm}"`);
  } catch (err) {
    console.error('❌ Falha ao registrar busca:', err);
  }
}, 500);
    return () => clearTimeout(handler);
  }, [searchTerm, slug]);

  const [products, setProducts] = useState<Product[]>(initialProducts || []);
  const [bundles, setBundles] = useState<Bundle[]>(initialBundles || []);

  // compute which catalogs currently have items
  const availableCatalogs: CatalogType[] = React.useMemo(() => {
    const list: CatalogType[] = [];
    if (services && services.length > 0) list.push('services');
    if (products && products.length > 0) list.push('products');
    if (bundles && bundles.length > 0) list.push('bundles');
    return list;
  }, [services, products, bundles]);

 useEffect(() => {
  console.log("Company data received:", {
    hasBankAccounts: !!company?.bankAccounts?.length,
    hasMobileWallets: !!company?.mobileWallets,
    bankAccounts: company?.bankAccounts,
    mobileWallets: company?.mobileWallets
  });
}, [company]);

  // ensure activeCatalog is always one of the available ones when data changes
  useEffect(() => {
    if (availableCatalogs.length === 0) return;
    if (!availableCatalogs.includes(activeCatalog)) {
      setActiveCatalog(availableCatalogs[0]);
    }
  }, [availableCatalogs, activeCatalog]);

  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isSearchOpen, setIsSearchOpen] = useState(false);


  const [client, setClient] = useState<ClientInfo>({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    taxId: '',
    vatNumber: '',
    billingAddress: { street: '', city: '', country: 'Moçambique' }
  });

  const [requestedInstallments, setRequestedInstallments] = useState(1);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [requestIntent, setRequestIntent] = useState<'quotation' | 'invoice'>('quotation');
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'mpesa'|'emola'|'visa'|'transfer'|'none'>('none');
  const [mobileMoneyPhone, setMobileMoneyPhone] = useState('');
  const [showAwaitingConfirmation, setShowAwaitingConfirmation] = useState(false);
  const [awaitingRef, setAwaitingRef] = useState<string>('');
  const [pollStatus, setPollStatus] = useState<'waiting'|'confirmed'|'failed'>('waiting');
  const [pollAttempts, setPollAttempts] = useState(0);
const [visibleServices, setVisibleServices] = useState(3);
const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const currency = company.currency || 'MT';

    // ------------ alterações -------------
  // controla o “ver mais” de cada secção
  const [showAllProducts, setShowAllProducts] = useState(false);


  // guarda o tipo do item que está aberto no modal (usado mais abaixo)
  const [selectedItemType, setSelectedItemType] = useState<CatalogType | null>(null);

  const openItem = (item: any, type: CatalogType) => {
    setSelectedItem(item);
    setSelectedItemType(type);
    setCarouselIndex(0);
  };

    // listas filtradas individualmente
  const filteredProducts = products.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description?.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const filteredServices = services.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description?.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const filteredBundles = bundles.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.shortDescription?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Only load if initial data is not provided (public portal provides all data)
 useEffect(() => {
  if (initialServices?.length && initialProducts?.length && initialBundles?.length) {
    return;
  }
  
  const loadCatalog = async () => {
    try {
      const promises = [];
      if (!initialServices?.length) promises.push(api.services.getAll());
      if (!initialProducts?.length) promises.push(api.products.getAll());
      if (!initialBundles?.length) promises.push(api.bundles.getAll());
      
      if (promises.length === 0) return;
      
      const results = await Promise.all(promises);
      console.log("Produtos vindos da API:", results[1]);
      // LOGS DE DEPURAÇÃO
      console.log("--- Resposta Bruta da API ---");
      console.log("Serviços:", results[0]);
      console.log("Produtos:", results[1]);
      console.log("Bundles (Combos/Subs):", results[2]);
      
      // Verifica se os campos esperados existem dentro do primeiro bundle (se houver)
      if (results[2] && results[2].length > 0) {
        console.log("Estrutura do primeiro Bundle:", results[2][0]);
        console.log("Itens populados?", results[2][0].items);
      }

      if (!initialServices?.length && results[0]) setServices(results[0]);
      if (!initialProducts?.length && results[1]) setProducts(results[1]);
      if (!initialBundles?.length && results[2]) setBundles(results[2]);
      
    } catch (err) {
      console.error('Erro detalhado ao carregar catálogo:', err);
    }
  };
  loadCatalog();
}, []);
  // Reset carousel index whenever a new item is selected
  useEffect(() => {
    setCarouselIndex(0);
  }, [selectedItem]);

 const total = useMemo(() => {
  const subtotal = cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  
  // Calculamos quanto custa apenas as taxas de encomenda (se houver)
  const orderFeesTotal = cart.reduce((acc, item) => {
    if (item.wantsOrder && item.orderPrice) {
      return acc + (Number(item.orderPrice) * item.quantity);
    }
    return acc;
  }, 0);

  // Se houver qualquer item com taxa de encomenda ativa, o Grand Total vira apenas o valor das taxas
  // Caso contrário, é o subtotal normal.
  const isPayingFees = cart.some(item => item.wantsOrder && Number(item.orderPrice) > 0);
  const grandTotal = isPayingFees ? orderFeesTotal : subtotal;

  return { subtotal, orderFeesTotal, grandTotal, isPayingFees };
}, [cart]);



  // Image helper (products & bundles have real images, services use placeholder – add image field to Service later)
  const getItemImage = (item: any, type: CatalogType): string | undefined => {
    // Products and bundles keep previous logic. For services, prefer real images from DB if available.
    if (type === 'products' && item.images?.length > 0) return getImageUrl(item.images[0]);
    if (type === 'bundles' && item.image) return getImageUrl(item.image);
    if (type === 'services' && item.images?.length > 0) return getImageUrl(item.images[0]);
    return undefined;
  };

  const getItemPrice = (item: any): number => {
  if (!item) return 0;

  const price = 
    item.billingPricePerCycle ?? 
    item.basePrice ?? 
    item.price ?? 
    0;

  // Debug opcional (remove depois de testar)
  // console.log(`getItemPrice for "${item.name}" (type: ${item.type || 'unknown'}):`, price);

  return Number(price);
};

  function getItemDescription(item: any): string {
    return 'shortDescription' in item ? (item as any).shortDescription || '' :
      ('description' in item ? (item as any).description || '' : '');
  }

  // Cart actions
const addToCart = (item: any, type: CatalogType) => {
  // Proteção contra stock zero (exceto madeToOrder)
  if (item.stockQuantity <= 0 && !item.madeToOrder) {
    toast.error(`Produto ${item.name} sem stock disponível`);
    return;
  }

  const existingIndex = cart.findIndex(c => c.itemId === item._id);

  if (existingIndex !== -1) {
    const updated = [...cart];
    updated[existingIndex].quantity += 1;
    setCart(updated);
  } else {
    setCart([...cart, {
      itemId: item._id,
      type,
      quantity: 1,
      name: item.name,
      price: getItemPrice(item),
      image: getItemImage(item, type),

      // === CAMPOS CRUCIAIS PARA MADE TO ORDER ===
      madeToOrder: item.madeToOrder, 
    orderPrice: item.orderPrice,
    deliveryDays: item.deliveryDays,
      wantsOrder: false,                          // começa desmarcado
    }]);
  }

  toast.success(`${item.name} adicionado ao carrinho`, { position: 'top-center' });
};

   const hasOrderWithPrice = cart.some(item => 
  item.wantsOrder === true && Number(item.orderPrice || 0) > 0
);

  const updateCartQuantity = (itemId: string, newQty: number) => {
    if (newQty < 1) return;
    setCart(cart.map(item => item.itemId === itemId ? { ...item, quantity: newQty } : item));
  };

  const removeFromCart = (itemId: string) => {
    setCart(cart.filter(item => item.itemId !== itemId));
  };

  // Totals (exactly your original logic)
  const calculateTotals = () => {
    let subtotal = 0;
    let minAllowed = 99;
    let maxPenaltyPct = 0;

    cart.forEach(cartItem => {
      subtotal += cartItem.price * cartItem.quantity;
      const svc = services.find(s => s._id === cartItem.itemId);
      if (svc) {
        minAllowed = Math.min(minAllowed, svc.allowedInstallments || 3);
        maxPenaltyPct = Math.max(maxPenaltyPct, svc.penaltyPercentagePerInstallment || 0);
      }
    });

    let penalty = 0;
    if (requestedInstallments > minAllowed) {
      const extra = requestedInstallments - minAllowed;
      penalty = subtotal * (extra * (maxPenaltyPct / 100));
    }

    return { subtotal, penalty, grandTotal: subtotal + penalty };
  };

  const totals = calculateTotals();

  // Dynamic max installments (your original logic)
  const getMaxAllowedInstallments = (): number => {
    let minAllowed = Infinity;
    let hasService = false;
    cart.forEach(cartItem => {
      const svc = services.find(s => s._id === cartItem.itemId);
      if (svc) {
        hasService = true;
        minAllowed = Math.min(minAllowed, svc.allowedInstallments || 3);
      }
    });
    return hasService ? Math.max(1, minAllowed) : 12;
  };

  const hasValidItems = cart.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasValidItems) {
      toast.error('Adicione pelo menos um item ao carrinho');
      return;
    }
    if (!client.name || !client.email) {
      toast.error('Nome da empresa e email são obrigatórios');
      return;
    }

    setSubmitting(true);

    try {
     // Dentro de handleSubmit, substitui o payload por:
      const payload = {
        companyId: company._id,
        clientData: { ...client, origin: 'external' },
        requisitionData: {
          items: cart.map(cartItem => ({
            itemType: cartItem.type === 'services' ? 'service' : 
                      cartItem.type === 'products' ? 'product' : 'bundle',
            item: cartItem.itemId,
            isMadeToOrder: cartItem.wantsOrder,
            priceAtOrder: cartItem.price,
            feePaid: cartItem.wantsOrder ? cartItem.orderPrice : 0,
            quantity: cartItem.quantity
          })),
          paymentMethod,
          totalAmount: total.subtotal,
          amountPaid: total.isPayingFees ? total.orderFeesTotal : 0,
          requestedInstallments,
          deliveryDate,
          requestIntent,
          notes
        }
      };

      await api.requisitions.submitPublic(payload);

      setShowSuccessModal(true);
      toast.success('Requisição enviada com sucesso!');

      // Reset everything
      setCart([]);
      setClient({
        name: '', contactPerson: '', email: '', phone: '', taxId: '', vatNumber: '',
        billingAddress: { street: '', city: '', country: 'Moçambique' }
      });
      setRequestedInstallments(1);
      setDeliveryDate('');
      setRequestIntent('quotation');
      setNotes('');
      setIsCartOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Falha ao enviar');
    } finally {
      setSubmitting(false);
    }
  };
useEffect(() => {
  if (selectedItem?.items) {
    console.log("ESTRUTURA FINAL NO FRONTEND:", JSON.stringify(selectedItem.items, null, 2));
  }
}, [selectedItem]);
 const handlePayment = async () => {
  if (!paymentMethod || paymentMethod === 'none') {
    toast.error('Selecione um método de pagamento');
    return;
  }
  if (!hasValidItems) {
    toast.error('Adicione pelo menos um item ao carrinho');
    return;
  }
  if (!client.name || !client.email) {
    toast.error('Preencha o nome e email para processar o pagamento');
    return;
  }

  // Mobile money prefix validation
  if (paymentMethod === 'mpesa' || paymentMethod === 'emola') {
    if (!mobileMoneyPhone) {
      toast.error(`Número de ${paymentMethod === 'mpesa' ? 'M-Pesa' : 'E-Mola'} é obrigatório`);
      return;
    }
    const cleaned = mobileMoneyPhone.replace(/\D/g, '');
    const isMpesa = paymentMethod === 'mpesa';
    const isEmola = paymentMethod === 'emola';
    const validMpesa = cleaned.startsWith('84') || cleaned.startsWith('85');
    const validEmola = cleaned.startsWith('86') || cleaned.startsWith('87');
    if (isMpesa && !validMpesa) {
      toast.error('Número M-Pesa deve começar com 84 ou 85');
      return;
    }
    if (isEmola && !validEmola) {
      toast.error('Número E-Mola deve começar com 86 ou 87');
      return;
    }
  }

  setSubmitting(true);
  try {
    const effectivePhone = (paymentMethod === 'mpesa' || paymentMethod === 'emola')
      ? mobileMoneyPhone : client.phone;
    const payload = {
      totalAmount: totals.grandTotal,
      method: paymentMethod,
      customer: {
        name: client.name,
        phone: effectivePhone,
        email: client.email,
      },
      companyId: company._id,
      currency: currency,
      items: cart.map(ci => ({
        itemId: ci.itemId,
        name: ci.name,
        quantity: ci.quantity,
        price: ci.price,
        type: ci.type
      }))
    };
    const resp = await api.checkout.process(payload, true);
    console.log('✅ Resposta do checkout:', resp);
    if (resp?.success) {
      if (resp.awaiting_confirmation || (resp.status === 'pending' && (paymentMethod === 'mpesa' || paymentMethod === 'emola'))) {
        setAwaitingRef(resp.externalRef);
        setPollStatus('waiting');
        setPollAttempts(0);
        setShowAwaitingConfirmation(true);
        return;
      }
      if (resp.url) {
        window.location.href = resp.url;
      } else {
        toast.success(resp.message || 'Pagamento processado com sucesso!');
        setTimeout(() => {
          window.location.href = `/order-success?ref=${resp.externalRef}`;
        }, 1500);
      }
    } else {
      toast.error(resp?.message || 'Não foi possível iniciar o pagamento');
    }
  } catch (err: any) {
    console.error('Payment error:', err);
    if (err.message?.includes('504') || err.response?.status === 504) {
      toast.error('O serviço de pagamento está demorando muito. Tente novamente.');
    } else {
      toast.error(err.message || 'Erro ao processar pagamento');
    }
  } finally {
    setSubmitting(false);
  }
};

    // Poll awaiting confirmation
  useEffect(() => {
    if (!showAwaitingConfirmation || !awaitingRef) return;
    let cancelled = false;
    setPollStatus('waiting');
    setPollAttempts(0);

    const interval = setInterval(async () => {
      try {
        const res = await api.checkout.transactionStatus(awaitingRef);
        if (cancelled) return;
        if (res?.found && res.transaction) {
          if (res.transaction.status === 'success') {
            setPollStatus('confirmed');
            clearInterval(interval);
            setTimeout(() => {
              setShowAwaitingConfirmation(false);
              setShowSuccessModal(true);
            }, 1500);
            return;
          } else if (res.transaction.status === 'failed') {
            setPollStatus('failed');
            clearInterval(interval);
            setTimeout(() => setShowAwaitingConfirmation(false), 3000);
            return;
          }
        } else if (res?.status === 'success' || res?.status === 'completed') {
          setPollStatus('confirmed');
          clearInterval(interval);
          setTimeout(() => {
            window.location.href = `/order-success?ref=${awaitingRef}`;
          }, 2000);
          return;
        } else if (res?.status === 'failed' || res?.status === 'cancelled') {
          setPollStatus('failed');
          clearInterval(interval);
          return;
        }
        setPollAttempts(p => p + 1);
      } catch {}
    }, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [showAwaitingConfirmation, awaitingRef]);

  const closeSuccessModal = () => setShowSuccessModal(false);

  return (
    <div className="min-h-screen bg-[#f8f7f2] text-[#1a1a1a] font-sans overflow-x-hidden">
     {/* Top Announcement Bar */}
      <div className="w-full bg-[#0b1f28] py-2 text-center text-[10px] md:text-xs text-white/80 uppercase tracking-widest rounded-t-2xl mb-2">
        Subscribe to our Newsletter for latest updates & collections ↗
      </div>

      {/* Main Container Hero Wrapper with Full-Bleed Background */}
      <div className="relative rounded-[28px] md:rounded-[40px] overflow-hidden min-h-[680px] lg:min-h-[750px] shadow-2xl flex flex-col justify-between">
        
        {/* Hero Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src={getImageUrl(portalContent?.hero?.backgroundImage) || "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&q=80"} 
            alt="Cargo Ship Logistics" 
            className="w-full h-full object-cover object-center"
          />
          {/* Subtle gradient overlay to enhance text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/20" />
        </div>

        {/* Dynamic Header / Navigation overlay */}
        <header className="relative z-50 w-full px-4 md:px-8 pt-4 md:pt-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            
            {/* Logo Left */}
            <div className="flex items-center">
              {company.logo ? (
                <img src={company.logo} alt={company.name} className="h-7 md:h-9 w-auto" />
              ) : (
                <span className="font-extrabold text-2xl md:text-3xl text-white tracking-tight">
                  {company.name || "uthao"}
                </span>
              )}
            </div>

            {/* Desktop Navigation Center Pill (Frosted Glass) */}
            <nav className={`hidden lg:flex items-center gap-1 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full transition-all duration-300 ${isSearchOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
              {['Home', 'Services', 'Resources', 'About', 'Career'].map((item) => (
                <a 
                  key={item} 
                  href={`#${item.toLowerCase()}`} 
                  className="px-4 py-1.5 rounded-full text-xs font-medium text-white/90 hover:text-white hover:bg-white/15 transition-all whitespace-nowrap"
                >
                  {item}
                </a>
              ))}
            </nav>

            {/* Right Action Menu: Search, Cart, CTA Button & Mobile Toggle */}
            <div className="flex items-center gap-2 md:gap-3">
              
              {/* Search Toggle / Input Bar */}
              {!isSearchOpen ? (
                <button 
                  onClick={() => setIsSearchOpen(true)} 
                  aria-label="Search"
                  className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 rounded-full text-white transition-all"
                >
                  <Search size={16} />
                </button>
              ) : (
                <div className="flex items-center gap-2 bg-white/90 backdrop-blur-lg border border-white px-3 py-1.5 rounded-full animate-in fade-in zoom-in-95 duration-200 w-[180px] sm:w-[240px] md:w-[300px] shadow-lg">
                  <Search size={16} className="text-gray-500 shrink-0" />
                  <input 
                    autoFocus
                    type="text"
                    placeholder="Search services..."
                    className="bg-transparent border-none outline-none text-xs w-full min-w-0 placeholder:text-gray-500 text-gray-900 font-medium"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button onClick={() => { setIsSearchOpen(false); setSearchTerm(''); }} className="text-gray-500 hover:text-red-500 transition-colors shrink-0">
                    <X size={16} />
                  </button>
                </div>
              )}

              {/* Cart / Briefcase Button */}
              <button 
                onClick={() => setIsCartOpen(true)} 
                className="relative p-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 rounded-full text-white transition-all"
              >
                <Briefcase size={18} />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {cart.length}
                  </span>
                )}
              </button>

              {/* Get Started CTA */}
              <button className="hidden sm:flex items-center gap-1.5 px-5 py-2.5 bg-[#FF4500] hover:bg-[#E03E00] text-white rounded-full text-xs font-bold transition-all shadow-md hover:shadow-lg">
                <span>Get Started</span>
                <ArrowUpRight size={14} />
              </button>

              {/* Mobile Hamburger Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-9 h-9 flex items-center justify-center bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white hover:bg-white/20 transition-colors"
                aria-label="Menu"
              >
                <Menu size={16} />
              </button>
            </div>
          </div>

          {/* Mobile Drawer Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-3 p-4 bg-black/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl transition-all">
              <div className="flex flex-col gap-2">
                {['Home', 'Services', 'Resources', 'About', 'Career'].map((item) => (
                  <a
                    key={item}
                    href={`#${item.toLowerCase()}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2.5 bg-white/5 rounded-xl text-xs font-medium text-white hover:bg-white/10 transition-all border border-white/5"
                  >
                    {item}
                  </a>
                ))}
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-full mt-2 py-3 bg-[#FF4500] text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1"
                >
                  Get Started <ArrowUpRight size={14} />
                </button>
              </div>
            </div>
          )}
        </header>

        {/* Hero Section Content Body */}
        <section className="relative z-10 max-w-7xl w-full mx-auto px-4 md:px-8 py-10 md:py-16 grid grid-cols-1 lg:grid-cols-12 items-end gap-8">
          
          {/* Left Headline Column */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white/90 text-xs tracking-wide">
              {portalContent?.hero?.badge || "Unmatched Worldwide Reach"}
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold text-white leading-[1.08] tracking-tight">
              {portalContent?.hero?.headline || "Global ocean cargo — Efficient, on time and trusted"}
            </h1>

            <p className="text-white/80 max-w-lg text-sm md:text-base leading-relaxed font-light">
              {portalContent?.hero?.subheadline || "We provide top-notch residential and commercial services tailored to your logistics needs. Trusted, trained, and always on time."}
            </p>
          </div>

          {/* Right Logistics Schedules Widget (Glass Card) */}
          <div className="lg:col-span-5 w-full flex justify-end">
            <div className="w-full max-w-md bg-slate-900/40 backdrop-blur-xl border border-white/15 p-4 sm:p-5 rounded-2xl md:rounded-3xl shadow-2xl space-y-3">
              
              {/* Widget Tabs */}
              <div className="grid grid-cols-2 bg-white/10 p-1 rounded-xl gap-1 text-xs font-medium">
                <button className="py-2 text-white/70 hover:text-white rounded-lg transition-colors">
                  Tracking
                </button>
                <button className="py-2 bg-[#FFB800] text-gray-950 font-bold rounded-lg shadow-sm">
                  Schedules
                </button>
              </div>

              {/* Form Input Fields */}
              <div className="space-y-2 relative">
                <div className="flex items-center gap-3 bg-white/10 border border-white/10 p-3 rounded-xl text-white text-xs">
                  <MapPin size={16} className="text-white/60 shrink-0" />
                  <span className="truncate">Boston, United States (BDCGP)</span>
                </div>

                {/* Swap Icon */}
                <button className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-7 h-7 bg-[#FFB800] text-gray-900 rounded-full flex items-center justify-center shadow-md hover:scale-105 transition-transform">
                  <ArrowUpDown size={14} />
                </button>

                <div className="flex items-center gap-3 bg-white/10 border border-white/10 p-3 rounded-xl text-white text-xs">
                  <MapPin size={16} className="text-white/60 shrink-0" />
                  <span className="truncate">Singapore, Singapore (SGSIN)</span>
                </div>
              </div>

              {/* Date & Search Button Row */}
              <div className="grid grid-cols-12 gap-2 pt-1">
                <div className="col-span-6 flex items-center justify-between bg-white/10 border border-white/10 px-3 py-2.5 rounded-xl text-white text-xs">
                  <span className="text-white/80">29 Aug, 2026</span>
                  <Calendar size={14} className="text-white/60" />
                </div>
                <button className="col-span-6 bg-[#FF4500] hover:bg-[#E03E00] text-white text-xs font-bold rounded-xl py-2.5 px-3 flex items-center justify-center gap-1.5 transition-colors shadow-md">
                  <span>Search</span>
                  <ArrowUpRight size={14} />
                </button>
              </div>

            </div>
          </div>

        </section>

      </div>

      {/* OVERLAY DE PESQUISA */}
      {isSearchOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[55] pointer-events-none transition-opacity" />
      )}

      {/* ABOUT SECTION */}
{portalContent?.about?.enabled && (
  <section id="about" className="py-16 md:py-24 bg-white overflow-hidden text-gray-900">
    <div className="max-w-7xl mx-auto px-4 md:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        
        {/* Left Column: Text & Features */}
        <div className="lg:col-span-6 space-y-6 md:space-y-8">
          
          {/* Subtitle / Category Label */}
          <div className="flex items-center gap-2">
            <span className="text-[#FF4500] font-bold text-xs uppercase tracking-wider">
              ✦ About Us ✦
            </span>
          </div>

          {/* Heading */}
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-gray-900 tracking-tight">
            {portalContent.about.title ? (
              portalContent.about.title
            ) : (
              <>
                Driven by Trust, Powered <br className="hidden sm:block" />
                by <span className="text-[#FF4500]">Experience.</span>
              </>
            )}
          </h2>

          {/* Body Text */}
          <div 
            className="text-sm md:text-base leading-relaxed text-gray-600 max-w-xl font-normal" 
            dangerouslySetInnerHTML={{ 
              __html: portalContent.about.body || 'We deliver reliable logistics solutions that connect businesses and individuals across the globe, ensuring efficiency, safety, and trust in every move.' 
            }} 
          />

          {/* Feature Badges Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 pt-2">
            
            {/* Feature 1 */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF4500]/10 flex items-center justify-center shrink-0">
                <Globe className="text-[#FF4500]" size={20} />
              </div>
              <div>
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wide">Worldwide Service</h4>
                <p className="text-xs text-gray-500 mt-0.5">Seamless logistics coverage across countries and continents.</p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF4500]/10 flex items-center justify-center shrink-0">
                <Headphones className="text-[#FF4500]" size={20} />
              </div>
              <div>
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wide">24/7 Online Support</h4>
                <p className="text-xs text-gray-500 mt-0.5">Always available to assist you day or night.</p>
              </div>
            </div>

          </div>

          {/* Actions & Founder Bio Bar */}
          <div className="flex flex-wrap items-center gap-6 pt-4 border-t border-gray-100">
            {/* CTA Button */}
            <button className="px-7 py-3.5 bg-[#FF4500] hover:bg-[#e03d00] text-white font-bold rounded-full transition-all shadow-md flex items-center gap-2 text-xs md:text-sm">
              <span>Read More</span>
              <div className="w-6 h-6 rounded-full bg-black/20 flex items-center justify-center">
                <ArrowRight size={14} />
              </div>
            </button>

            {/* Founder Avatar & Signature */}
            <div className="flex items-center gap-3">
              <img 
                src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=150" 
                alt="Founder" 
                className="w-11 h-11 rounded-full object-cover border-2 border-white shadow"
              />
              <div>
                <h5 className="text-xs font-bold text-gray-900">Chris Visuals</h5>
                <p className="text-[10px] text-gray-500 font-medium">Co-Founder</p>
              </div>
            </div>

            {/* Mockup Signature */}
            <div className="hidden sm:block opacity-60 italic text-xl font-serif text-gray-800 tracking-tighter">
              C. Visuals
            </div>
          </div>

        </div>

        {/* Right Column: Angled Image Box with Floating Badge */}
        <div className="lg:col-span-6 relative flex justify-end">
          
          {/* Angled Image Container */}
          <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] md:aspect-[16/9] lg:aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl [clip-path:polygon(8%_0%,_100%_0%,_100%_100%,_0%_100%)]">
            <img 
              src={getImageUrl(portalContent?.about?.image) || "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&q=80"} 
              alt="About Truck Driver Logistics" 
              className="w-full h-full object-cover object-center"
            />
          </div>

          {/* Floating Circular Badge on Edge */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 md:-translate-x-1/3 z-20 w-24 h-24 md:w-28 md:h-28 bg-white rounded-full p-2 shadow-2xl flex items-center justify-center border border-gray-100">
            <div className="relative w-full h-full rounded-full border border-dashed border-[#FF4500]/40 flex items-center justify-center animate-spin-slow">
              <span className="text-[8px] font-bold uppercase tracking-widest text-gray-600 text-center leading-none">
                • EXPLORE MORE • EXPLORE MORE
              </span>
            </div>
            <div className="absolute w-8 h-8 rounded-full bg-[#FF4500] text-white flex items-center justify-center shadow-md">
              <ArrowUpRight size={16} />
            </div>
          </div>

        </div>

      </div>
    </div>
  </section>
)}

    {/* SERVICES SECTION */}
{filteredServices.length > 0 && (
  <section id="services" className="py-16 md:py-24 bg-[#f8f9fa] relative overflow-hidden text-gray-900">
    
    {/* Subtle Dot-Matrix Logistics Background */}
    <div 
      className="absolute inset-0 opacity-[0.03] pointer-events-none" 
      style={{
        backgroundImage: 'radial-gradient(#000 1.5px, transparent 1.5px)', 
        backgroundSize: '24px 24px'
      }} 
    />

    <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
      
      {/* Grid Layout Container */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-stretch">
        
        {/* Section Header Card (First Slot in Grid) */}
        <div className="flex flex-col justify-center space-y-4 pr-0 lg:pr-4">
          <div className="flex items-center gap-2">
            <span className="text-[#FF4500] font-bold text-xs uppercase tracking-wider">
              ✦ Services ✦
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight tracking-tight">
            Our Trusted <br />
            <span className="text-gray-900">Logistics Services</span>
          </h2>

          <p className="text-gray-500 text-xs md:text-sm leading-relaxed max-w-sm">
            We provide comprehensive logistics solutions tailored to keep your supply chain moving seamlessly across borders and regions.
          </p>
        </div>

        {/* Dynamic Service Cards */}
        {filteredServices.slice(0, visibleServices).map((item, index) => {
          return (
            <div
              key={item._id || index}
              onClick={() => openItem(item, 'services')}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100/80 transition-all duration-300 flex flex-col cursor-pointer"
            >
              {/* Card Image Wrapper */}
              <div className="relative w-full h-48 md:h-52 overflow-hidden bg-gray-100">
                {item.images?.length > 0 ? (
                  <img 
                    src={getImageUrl(item.images[0])} 
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <img 
                    src={`https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=600`}
                    alt="Logistics service"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                )}

                {/* Popular Badge floating overlay if relevant */}
                {index === 2 && (
                  <div className="absolute top-3 right-3 z-10">
                    <span className="bg-[#FF4500] text-white text-[10px] px-3 py-1 rounded-full font-bold shadow-md uppercase tracking-wider">
                      Popular
                    </span>
                  </div>
                )}
              </div>

              {/* Card Body Info */}
              <div className="p-5 md:p-6 flex items-start gap-4 flex-1">
                {/* Circular Orange Icon */}
                <div className="w-10 h-10 rounded-full bg-[#FF4500]/10 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-[#FF4500] transition-colors duration-300">
                  <Truck className="text-[#FF4500] group-hover:text-white transition-colors duration-300" size={18} />
                </div>

                {/* Content */}
                <div className="space-y-1.5 flex-1">
                  <h3 className="text-sm md:text-base font-bold text-gray-900 group-hover:text-[#FF4500] transition-colors duration-200">
                    {item.name}
                  </h3>
                  <p className="text-gray-500 text-xs leading-relaxed line-clamp-3">
                    {item.description || "Seamless logistics coverage designed to keep your shipments safe, efficient, and delivered right on time."}
                  </p>
                </div>
              </div>
            </div>
          );
        })}

      </div>

      {/* Expand / Load More Button */}
      {visibleServices < filteredServices.length && (
        <div className="mt-12 md:mt-16 flex justify-center">
          <button 
            onClick={() => setVisibleServices(prev => prev + 3)}
            className="group flex items-center gap-2.5 px-8 py-3.5 bg-[#FF4500] hover:bg-[#e03d00] text-white rounded-full font-bold transition-all shadow-md hover:shadow-lg text-xs md:text-sm"
          >
            <span>Expand Services</span>
            <Plus size={16} className="group-hover:rotate-90 transition-transform duration-300" />
          </button>
        </div>
      )}

    </div>
  </section>
)}

    {/* MISSION, VISION, VALUES */}
{portalContent?.missionVision?.enabled && (
  <section className="py-16 md:py-24 bg-[#f8f9fa] px-4 md:px-8">
    <div className="max-w-7xl mx-auto bg-[#0b1f28] rounded-[28px] md:rounded-[40px] pt-14 md:pt-20 pb-20 md:pb-24 px-6 md:px-12 relative overflow-hidden text-center shadow-2xl">
      
      {/* Background Dot Grid Accents */}
      <div 
        className="absolute inset-0 opacity-10 pointer-events-none" 
        style={{
          backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', 
          backgroundSize: '24px 24px'
        }} 
      />

      {/* Header Badge & Title */}
      <div className="relative z-10 mb-12 md:mb-16 space-y-3">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-white/90 text-xs font-medium tracking-wide">
          <span className="text-[#FF4500]">✦</span> Why Choose Us <span className="text-[#FF4500]">✦</span>
        </div>

        <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
          What Makes Us the <br />
          <span className="text-[#FF4500]">Right Choice</span>
        </h2>
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10 text-left">
        
        {/* Mission Card */}
        <div className="group bg-white p-6 md:p-8 rounded-2xl transition-all duration-300 hover:-translate-y-1.5 shadow-lg border border-gray-100 relative overflow-hidden flex flex-col justify-between">
          <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF4500] opacity-0 group-hover:opacity-100 transition-opacity" />
          
          <div>
            <div className="w-12 h-12 bg-[#FF4500]/10 rounded-xl flex items-center justify-center mb-6 text-[#FF4500] group-hover:bg-[#FF4500] group-hover:text-white transition-colors duration-300">
              <Target size={22} />
            </div>
            
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">
              {portalContent?.missionVision?.mission?.title || "Mission"}
            </h3>
            
            <p className="text-gray-600 text-xs md:text-sm leading-relaxed mb-6">
              {portalContent?.missionVision?.mission?.content || "To provide strategic intelligence and logistics excellence that empower our clients to navigate complex supply chains with absolute confidence."}
            </p>
          </div>

          <div>
            <button className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#FF4500] hover:text-[#e03d00] border-b-2 border-[#FF4500]/30 hover:border-[#FF4500] pb-0.5 transition-all">
              <span>Learn More</span>
              <ArrowUpRight size={14} />
            </button>
          </div>
        </div>

        {/* Vision Card */}
        <div className="group bg-white p-6 md:p-8 rounded-2xl transition-all duration-300 hover:-translate-y-1.5 shadow-lg border border-gray-100 relative overflow-hidden flex flex-col justify-between">
          <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF4500] opacity-0 group-hover:opacity-100 transition-opacity" />

          <div>
            <div className="w-12 h-12 bg-[#FF4500]/10 rounded-xl flex items-center justify-center mb-6 text-[#FF4500] group-hover:bg-[#FF4500] group-hover:text-white transition-colors duration-300">
              <Eye size={22} />
            </div>

            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">
              {portalContent?.missionVision?.vision?.title || "Vision"}
            </h3>

            <p className="text-gray-600 text-xs md:text-sm leading-relaxed mb-6">
              {portalContent?.missionVision?.vision?.content || "To be recognized globally as the gold standard of freight forwarding, bridging global markets through seamless technological innovation."}
            </p>
          </div>

          <div>
            <button className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#FF4500] hover:text-[#e03d00] border-b-2 border-[#FF4500]/30 hover:border-[#FF4500] pb-0.5 transition-all">
              <span>Learn More</span>
              <ArrowUpRight size={14} />
            </button>
          </div>
        </div>

        {/* Core Values Card */}
        <div className="group bg-white p-6 md:p-8 rounded-2xl transition-all duration-300 hover:-translate-y-1.5 shadow-lg border border-gray-100 relative overflow-hidden flex flex-col justify-between md:col-span-2 lg:col-span-1">
          <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF4500] opacity-0 group-hover:opacity-100 transition-opacity" />

          <div>
            <div className="w-12 h-12 bg-[#FF4500]/10 rounded-xl flex items-center justify-center mb-6 text-[#FF4500] group-hover:bg-[#FF4500] group-hover:text-white transition-colors duration-300">
              <ShieldCheck size={22} />
            </div>

            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">
              {portalContent?.missionVision?.values?.title || "Core Values"}
            </h3>

            <ul className="grid grid-cols-2 gap-3 mb-6">
              {(portalContent?.missionVision?.values?.items?.length > 0 
                ? portalContent.missionVision.values.items 
                : ["Integrity", "Innovation", "Precision", "Safety"]
              ).map((value, i) => (
                <li key={i} className="flex items-center gap-2 text-xs font-bold text-gray-700">
                  <div className="w-2 h-2 rounded-full bg-[#FF4500] shrink-0" />
                  <span>{value}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <button className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#FF4500] hover:text-[#e03d00] border-b-2 border-[#FF4500]/30 hover:border-[#FF4500] pb-0.5 transition-all">
              <span>Learn More</span>
              <ArrowUpRight size={14} />
            </button>
          </div>
        </div>

      </div>

      {/* Floating Bottom Call-To-Action Button */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 z-20">
        <button className="bg-[#FF4500] hover:bg-[#e03d00] text-white px-8 md:px-10 py-3.5 md:py-4 rounded-full font-bold uppercase tracking-wider text-xs md:text-sm shadow-xl hover:scale-105 transition-all flex items-center gap-2">
          <span>Get Started</span>
          <ArrowUpRight size={16} />
        </button>
      </div>

    </div>
  </section>
)}

  {/* PRODUCTS SECTION */}
{filteredProducts.length > 0 && (
  <section className="py-16 md:py-24 bg-[#f8f9fa] text-gray-900 relative overflow-hidden">
    
    {/* Subtle Dot-Matrix Background */}
    <div 
      className="absolute inset-0 opacity-[0.03] pointer-events-none" 
      style={{
        backgroundImage: 'radial-gradient(#000 1.5px, transparent 1.5px)', 
        backgroundSize: '24px 24px'
      }} 
    />

    <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
      
      {/* Header Block */}
      <div className="mb-12 md:mb-16 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-[#FF4500] font-bold text-xs uppercase tracking-wider">
              ✦ Curated Solutions ✦
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 tracking-tight leading-tight">
            Expert Solutions for <br />
            <span className="text-gray-900">Premium Operations</span>
          </h2>
        </div>

        <div className="flex flex-col sm:flex-row lg:items-center gap-4 md:gap-6">
          <p className="text-gray-500 text-xs md:text-sm max-w-md leading-relaxed">
            Explore our full range of professional toolkits and assets designed to keep every aspect of your business operating efficiently.
          </p>

          {/* Navigation Controls */}
          <div className="flex items-center gap-2 shrink-0">
            <button className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-white hover:shadow-md transition-all">
              <ChevronLeft size={18} />
            </button>
            <button className="w-9 h-9 rounded-full bg-[#FF4500] flex items-center justify-center text-white shadow-md hover:bg-[#e03d00] transition-all">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {(showAllProducts ? filteredProducts : filteredProducts.slice(0, 4)).map((item, index) => {
          const firstImage = Array.isArray(item.images) && item.images.length > 0 ? item.images[0] : item.image;
          const imageUrl = getImageUrl(firstImage || '');

          return (
            <div
              key={item._id || index}
              onClick={() => openItem(item, 'products')}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 transition-all duration-300 flex flex-col cursor-pointer"
            >
              {/* Image Container (Top) */}
              <div className="relative w-full h-48 md:h-52 overflow-hidden bg-gray-100">
                {imageUrl ? (
                  <img 
                    src={imageUrl} 
                    alt={item.name} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-50 text-gray-300">
                    <Package size={32} />
                  </div>
                )}

                {/* Category Badge Floating Overlay */}
                <div className="absolute top-3 left-3 z-10">
                  <span className="bg-white/90 backdrop-blur-md text-gray-700 text-[10px] px-3 py-1 rounded-full font-bold shadow-sm border border-gray-100 uppercase tracking-wider">
                    {item.category || 'Premium'}
                  </span>
                </div>

                {/* Arrow Button */}
                <div className="absolute top-3 right-3 z-10">
                  <div className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-md flex items-center justify-center text-gray-800 shadow-sm group-hover:bg-[#FF4500] group-hover:text-white transition-all duration-300">
                    <ArrowUpRight size={14} />
                  </div>
                </div>
              </div>

              {/* Product Info (Bottom) */}
              <div className="p-5 flex flex-col justify-between flex-1">
                <div>
                  <div className="flex items-start gap-3 mb-3">
                    {/* Circle Accent Icon */}
                    <div className="w-9 h-9 rounded-full bg-[#FF4500]/10 flex items-center justify-center shrink-0 text-[#FF4500] group-hover:bg-[#FF4500] group-hover:text-white transition-colors duration-300 mt-0.5">
                      <Package size={16} />
                    </div>

                    <h3 className="text-base font-bold text-gray-900 group-hover:text-[#FF4500] transition-colors duration-200 line-clamp-2 leading-snug">
                      {item.name}
                    </h3>
                  </div>

                  <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-4">
                    {item.description || "Sophisticated infrastructure designed for elite business operations."}
                  </p>
                </div>

                {/* Price Tag */}
                <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">
                    Price
                  </span>
                  <span className="text-sm font-extrabold text-gray-900">
                    {getItemPrice(item).toLocaleString()} {currency}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Expand / Toggle Products Button */}
      <div className="mt-12 md:mt-16 flex justify-center">
        <button 
          onClick={() => setShowAllProducts(prev => !prev)}
          className="group flex items-center gap-2.5 px-8 py-3.5 bg-[#FF4500] hover:bg-[#e03d00] text-white rounded-full font-bold transition-all shadow-md hover:shadow-lg text-xs md:text-sm"
        >
          <span>{showAllProducts ? 'Show Less' : 'Explore All Products'}</span>
          <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
        </button>
      </div>

    </div>
  </section>
)}

      {/* BUNDLES - COMBO */}
      {filteredBundles.some(b => b.type === 'Combo') && (
        <section className="py-16 md:py-24 bg-[#f8f7f2] relative overflow-hidden">
          <div className="absolute -bottom-20 -left-20 opacity-5 pointer-events-none">
            <Leaf size={200} className="text-[#0E7D83]" />
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
            <div className="mb-12 md:mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div className="max-w-xl">
                <div className="flex items-center gap-3 mb-3 md:mb-4">
                  <div className="w-8 md:w-12 h-[1px] bg-[#0E7D83]" />
                  <span className="text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] text-[#0E7D83] opacity-60">
                    Strategic Alliances
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0E7D83] tracking-tight leading-tight">
                  Comprehensive <span className="text-[#0a5e63]">Service Bundles</span>
                </h2>
              </div>
              <p className="text-gray-500 text-xs md:text-sm max-w-xs md:text-right">
                Integrated solutions designed to provide full protection and scalability for your business operations.
              </p>
            </div>

            <div className="flex gap-4 md:gap-8 overflow-x-auto pb-8 md:pb-12 pt-4 scrollbar-hide snap-x">
              {filteredBundles
                .filter(item => item.type === 'Combo')
                .map((item, index) => {
                  const image = getImageUrl(item.image || '');
                  return (
                    <div
                      key={item._id}
                      onClick={() => openItem(item, 'bundles')}
                      className="group relative min-w-[85vw] sm:min-w-[400px] md:min-w-[500px] lg:min-w-[750px] snap-center transition-all duration-500 cursor-pointer"
                    >
                      <div className="bg-white rounded-[2rem] md:rounded-[3.5rem] p-4 md:p-6 flex flex-col md:flex-row gap-6 md:gap-8 h-full border border-gray-100 shadow-sm hover:shadow-2xl transition-all">
                        <div className="w-full md:w-[300px] lg:w-[350px] h-[220px] md:h-[350px] lg:h-[400px] rounded-[1.8rem] md:rounded-[2.8rem] overflow-hidden relative shrink-0">
                          <img 
                            src={image} 
                            alt={item.name} 
                            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" 
                          />
                          <div className="absolute top-4 md:top-6 left-4 md:left-6 bg-[#E5E65D] text-[#0E7D83] text-[9px] md:text-[10px] font-black px-3 md:px-4 py-1.5 md:py-2 rounded-full uppercase tracking-tighter shadow-lg">
                            Best Value Pack
                          </div>
                        </div>

                        <div className="flex-1 flex flex-col justify-between py-2 md:py-4 pr-0 md:pr-4">
                          <div>
                            <div className="flex justify-between items-start mb-4 md:mb-6">
                              <span className="text-[10px] md:text-[11px] font-black text-[#0a5e63] uppercase tracking-widest">
                                Bundle Solution {index + 1 < 10 ? `0${index + 1}` : index + 1}
                              </span>
                              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#f8f7f2] flex items-center justify-center text-[#0E7D83] group-hover:bg-[#E5E65D] transition-colors duration-500">
                                <ArrowUpRight size={18} />
                              </div>
                            </div>

                            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#0E7D83] mb-4 md:mb-6 tracking-tight leading-none">
                              {item.name}
                            </h3>

                            <p className="text-gray-500 text-xs md:text-sm leading-relaxed mb-6 md:mb-8 line-clamp-3">
                              {item.description || "A cohesive selection of professional frameworks designed to scale your business with maximum efficiency."}
                            </p>
                          </div>

                          <div className="flex flex-col sm:flex-row sm:items-end justify-between border-t border-gray-50 pt-6 md:pt-8 gap-4 sm:gap-0">
                            <div className="space-y-1">
                              <span className="text-[9px] md:text-[10px] font-bold text-gray-300 uppercase tracking-widest block">Retainer Investment</span>
                              <div className="text-2xl md:text-3xl font-black text-[#0E7D83]">
                                {getItemPrice(item).toLocaleString()} <span className="text-[10px] opacity-40 font-medium">{currency}</span>
                              </div>
                            </div>

                            <button className="bg-[#0E7D83] text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-[10px] md:text-xs font-bold uppercase tracking-widest flex items-center gap-3 hover:bg-[#0a5e63] transition-all w-full sm:w-auto justify-center">
                              Get Bundle <Layers size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>

            <div className="flex flex-col items-center mt-8 md:mt-12 gap-3 md:gap-4">
              <div className="w-32 md:w-48 h-[3px] bg-gray-200 rounded-full overflow-hidden">
                <div className="bg-[#0E7D83] h-full w-1/3 rounded-full" />
              </div>
              <span className="text-[9px] md:text-[10px] font-black text-[#0E7D83] uppercase tracking-[0.4em]">
                Scroll to explore
              </span>
            </div>
          </div>
        </section>
      )}

     {/* SUBSCRIPTIONS */}
{filteredBundles.some(b => b.type === 'Subscription') && (
  <section className="py-16 md:py-24 bg-[#f8f9fa] text-gray-900 relative overflow-hidden">
    
    {/* Subtle Dot-Matrix Background Accent */}
    <div 
      className="absolute inset-0 opacity-[0.03] pointer-events-none" 
      style={{
        backgroundImage: 'radial-gradient(#000 1.5px, transparent 1.5px)', 
        backgroundSize: '24px 24px'
      }} 
    />

    <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
      
      {/* Header Section */}
      <div className="flex flex-col items-center text-center mb-12 md:mb-16">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[#FF4500] font-bold text-xs uppercase tracking-wider">
            ✦ Simple Pricing ✦
          </span>
        </div>

        <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-3 tracking-tight">
          Flexible Subscription Plans
        </h2>

        <p className="text-gray-500 text-xs md:text-sm max-w-md leading-relaxed mb-8">
          Choose the perfect plan tailored to your operational scale. Transparent pricing with no hidden fees.
        </p>

        {/* Toggle Billing Switch */}
        <div className="inline-flex items-center gap-3 p-1.5 bg-white border border-gray-200/80 rounded-full shadow-sm">
          <span className="text-xs font-bold text-gray-500 pl-3">Yearly</span>
          <div className="w-12 h-6 bg-gray-100 rounded-full p-0.5 flex items-center relative cursor-pointer border border-gray-200">
            <div className="w-5 h-5 bg-[#FF4500] rounded-full shadow-sm transition-transform" />
          </div>
          <span className="text-xs font-bold text-gray-900 pr-3 flex items-center gap-1.5">
            Monthly
            <span className="bg-[#FF4500]/10 text-[#FF4500] text-[9px] font-extrabold px-2 py-0.5 rounded-full uppercase">
              Save 20%
            </span>
          </span>
        </div>
      </div>

      {/* Subscription Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
        {filteredBundles.filter(item => item.type === 'Subscription').map((plan, index) => {
          // Destaca o segundo cartão (ou meio) como plano em destaque
          const isFeatured = index === 1;

          return (
            <div 
              key={plan._id || index} 
              className={`relative p-6 md:p-8 rounded-2xl flex flex-col justify-between group transition-all duration-300 bg-white border ${
                isFeatured 
                  ? 'border-[#FF4500] shadow-xl md:-translate-y-2' 
                  : 'border-gray-200/80 shadow-sm hover:shadow-md'
              }`}
            >
              {/* Featured Badge */}
              {isFeatured && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-20">
                  <span className="bg-[#FF4500] text-white text-[10px] font-extrabold uppercase tracking-widest px-4 py-1 rounded-full shadow-sm">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Card Top Info */}
              <div>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                    {plan.name}
                  </h3>
                  <div className="w-8 h-8 rounded-full bg-[#FF4500]/10 flex items-center justify-center text-[#FF4500]">
                    <ShieldCheck size={18} />
                  </div>
                </div>

                <p className="text-gray-500 text-xs leading-relaxed mb-6 line-clamp-2">
                  {plan.description || "Comprehensive plan tailored for individual logistics and business expansion."}
                </p>

                {/* Pricing Display */}
                <div className="flex items-baseline gap-1 pb-6 mb-6 border-b border-gray-100">
                  <span className="text-3xl md:text-4xl font-extrabold text-gray-900">
                    ${getItemPrice(plan).toLocaleString()}
                  </span>
                  <span className="text-xs md:text-sm font-semibold text-gray-400">/month</span>
                </div>

                {/* Features List */}
                <div className="space-y-3 mb-8">
                  <p className="text-[11px] font-extrabold text-gray-900 uppercase tracking-wider">
                    What's Included:
                  </p>
                  <ul className="space-y-3">
                    {plan.includedLimits?.map((limit: any, i: number) => (
                      <li key={i} className="flex items-start gap-2.5 text-xs text-gray-600">
                        <div className="w-4 h-4 rounded-full bg-[#FF4500]/10 flex items-center justify-center shrink-0 mt-0.5 text-[#FF4500]">
                          <Check size={10} strokeWidth={3} />
                        </div>
                        <span className="leading-tight">{limit.description}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Action CTA Button */}
              <button 
                onClick={() => { addToCart(plan, 'bundles'); }} 
                className={`w-full py-3.5 rounded-full font-bold text-xs uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2 ${
                  isFeatured 
                    ? 'bg-[#FF4500] hover:bg-[#e03d00] text-white shadow-md hover:shadow-lg' 
                    : 'bg-gray-900 hover:bg-gray-800 text-white'
                }`}
              >
                <span>Get Started Now</span>
                <ArrowUpRight size={14} />
              </button>

            </div>
          );
        })}
      </div>

    </div>
  </section>
)}

   {/* CLIENTS / PARTNERS */}
{portalContent?.clients?.enabled && portalContent.clients.items?.length > 0 && (
  <section className="py-12 md:py-16 bg-[#f8f9fa] border-b border-gray-200/60 relative overflow-hidden">
    
    {/* Subtle Dot-Matrix Background */}
    <div 
      className="absolute inset-0 opacity-[0.03] pointer-events-none" 
      style={{
        backgroundImage: 'radial-gradient(#000 1.5px, transparent 1.5px)', 
        backgroundSize: '24px 24px'
      }} 
    />

    <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
      <div className="flex flex-col items-center mb-8 md:mb-10 text-center">
        <span className="text-[#FF4500] font-bold text-xs uppercase tracking-wider">
          ✦ Trusted by Industry Leaders ✦
        </span>
      </div>

      {/* Logo Grid / Slider Wrapper */}
      <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10">
        {portalContent.clients.items.map((client: any, i: number) => (
          <div 
            key={i} 
            className="group relative px-6 py-3.5 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300 flex items-center justify-center"
          >
            <img 
              src={getImageUrl(client.logo)} 
              alt={client.name || 'Client Logo'} 
              className="h-6 md:h-8 w-auto object-contain grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300" 
            />
          </div>
        ))}
      </div>
    </div>
  </section>
)}

{/* TESTIMONIALS */}
{portalContent?.testimonials?.enabled && portalContent.testimonials.items?.length > 0 && (
  <section id="testimonials" className="py-16 md:py-24 bg-[#f8f9fa] relative overflow-hidden text-gray-900">
    
    {/* Background Watermark Accent */}
    <div className="absolute top-10 right-10 opacity-[0.03] pointer-events-none">
      <Quote size={280} className="text-gray-900" />
    </div>

    <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
      
      {/* Header */}
      <div className="flex flex-col items-center text-center mb-12 md:mb-16 space-y-2">
        <span className="text-[#FF4500] font-bold text-xs uppercase tracking-wider">
          ✦ Testimonials ✦
        </span>

        <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 tracking-tight">
          What Our Clients <span className="text-gray-900">Say About Us</span>
        </h2>

        <p className="text-gray-500 text-xs md:text-sm max-w-md leading-relaxed pt-1">
          Real feedback from companies that trust our global logistics networks every single day.
        </p>
      </div>

      {/* Testimonials Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-stretch">
        {portalContent.testimonials.items.map((t: any, i: number) => {
          const isFeatured = i === 1;

          return (
            <div 
              key={i} 
              className={`p-6 md:p-8 rounded-2xl flex flex-col justify-between transition-all duration-300 bg-white border relative ${
                isFeatured 
                  ? 'border-[#FF4500] shadow-xl md:-translate-y-2' 
                  : 'border-gray-200/80 shadow-sm hover:shadow-md'
              }`}
            >
              {/* Top Bar Accent */}
              <div className={`absolute top-0 left-6 right-6 h-1 rounded-b-md ${
                isFeatured ? 'bg-[#FF4500]' : 'bg-transparent'
              }`} />

              <div>
                {/* Rating Stars */}
                <div className="flex items-center gap-1 mb-6">
                  {Array.from({ length: t.rating || 5 }).map((_, k) => (
                    <Star key={k} size={14} className="fill-[#FF4500] text-[#FF4500]" />
                  ))}
                </div>

                {/* Testimonial Quote */}
                <blockquote className="mb-8">
                  <p className="text-gray-700 text-sm md:text-base font-medium leading-relaxed italic">
                    "{t.text}"
                  </p>
                </blockquote>
              </div>

              {/* Author / Client Info */}
              <div className="flex items-center gap-3.5 pt-5 border-t border-gray-100">
                <div className="w-11 h-11 rounded-full bg-gray-100 overflow-hidden shrink-0 border border-gray-200">
                  {t.avatar ? (
                    <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-bold text-xs uppercase bg-[#FF4500]/10 text-[#FF4500]">
                      {t.name?.substring(0, 2) || 'CL'}
                    </div>
                  )}
                </div>

                <div className="space-y-0.5">
                  <p className="font-bold text-sm text-gray-900 leading-snug">
                    {t.name}
                  </p>
                  <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
                    {t.role} {t.company && `• ${t.company}`}
                  </p>
                </div>
              </div>

            </div>
          );
        })}
      </div>

    </div>
  </section>
)}

    {/* ITEM DETAILS MODAL */}
{selectedItem && (
  <div className="fixed inset-0 z-[100] flex justify-end">
    {/* Dark Backdrop with Blur */}
    <div 
      className="absolute inset-0 bg-gray-900/50 backdrop-blur-md transition-opacity duration-500" 
      onClick={() => { setSelectedItem(null); setCurrentImgIndex(0); }} 
    />

    {/* Side Drawer Content Container */}
    <div className="relative w-full max-w-full lg:max-w-5xl bg-white shadow-2xl h-full overflow-y-auto z-10 animate-in slide-in-from-right duration-300 flex flex-col">
      
      {/* Floating Close Button */}
      <button 
        onClick={() => { setSelectedItem(null); setCurrentImgIndex(0); }} 
        className="fixed top-4 right-4 md:top-6 md:right-6 z-[110] w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white text-gray-700 hover:text-white hover:bg-[#FF4500] transition-all duration-300 shadow-md border border-gray-100"
      >
        <X size={20} strokeWidth={2.5} />
      </button>

      <div className="flex flex-col lg:flex-row min-h-full">
        
        {/* Left Column: Image Preview / Gallery */}
        <div className="relative w-full lg:w-1/2 bg-[#f8f9fa] p-6 md:p-8 lg:p-10 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-gray-100">
          <div>
            <div className="flex items-center gap-2 mb-6">
              <span className="text-[#FF4500] font-bold text-xs uppercase tracking-wider">
                ✦ Solution Details ✦
              </span>
            </div>

            {/* Image Box */}
            <div className="relative group">
              {(() => {
                const itemImages = selectedItem.images && Array.isArray(selectedItem.images) && selectedItem.images.length > 0
                  ? selectedItem.images 
                  : selectedItem.image ? [selectedItem.image] : [];
                const imageUrl = getImageUrl(itemImages[currentImgIndex] || '');
                
                return (
                  <div className="w-full">
                    <div className="w-full h-72 md:h-96 lg:h-[420px] rounded-2xl overflow-hidden bg-gray-100 shadow-sm border border-gray-200/80">
                      {imageUrl ? (
                        <img src={imageUrl} className="w-full h-full object-cover" alt={selectedItem.name} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <Package size={64} />
                        </div>
                      )}
                    </div>

                    {/* Image Thumbnails Dots/Controls */}
                    {itemImages.length > 1 && (
                      <div className="flex justify-center gap-2 mt-4">
                        {itemImages.map((_, idx) => (
                          <button 
                            key={idx}
                            onClick={() => setCurrentImgIndex(idx)}
                            className={`h-2 rounded-full transition-all duration-300 ${
                              idx === currentImgIndex ? 'w-8 bg-[#FF4500]' : 'w-2 bg-gray-300 hover:bg-gray-400'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Right Column: Product / Service Information */}
        <div className="flex-1 p-6 md:p-8 lg:p-12 flex flex-col justify-between bg-white">
          <div>
            {/* Category Badge & Rating */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] font-bold text-gray-700 uppercase tracking-wider px-3 py-1 bg-gray-100 rounded-full border border-gray-200">
                {selectedItem.category || 'Premium Service'}
              </span>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} size={12} className="fill-[#FF4500] text-[#FF4500]" />
                ))}
              </div>
            </div>

            {/* Title & Description */}
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight leading-tight">
              {selectedItem.name}
            </h3>

            <p className="text-gray-500 text-xs md:text-sm leading-relaxed mb-8">
              {selectedItem.description || "Sophisticated framework designed for modern operations with emphasis on efficiency and long-term scalability."}
            </p>

            {/* Core Provisions / Feature List */}
            <div className="space-y-3 mb-8">
              <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">
                Core Provisions
              </h4>
              <div className="divide-y divide-gray-100 border-t border-b border-gray-100">
                {(selectedItem.includedItems || selectedItem.items || []).slice(0, 5).map((it, i) => (
                  <div key={i} className="flex items-center gap-3 py-3 group">
                    <div className="w-5 h-5 rounded-full bg-[#FF4500]/10 flex items-center justify-center text-[#FF4500] shrink-0">
                      <Check size={11} strokeWidth={3} />
                    </div>
                    <span className="text-xs font-semibold text-gray-700">
                      {it.productId?.name || it.description || "Strategic Asset"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pricing & CTA Footer */}
          <div className="pt-6 border-t border-gray-100">
            <div className="flex flex-col gap-5">
              <div className="flex justify-between items-end">
                <div>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                    Investment Cost
                  </span>
                  <div className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 tracking-tight">
                    {getItemPrice(selectedItem).toLocaleString()} <span className="text-xs md:text-sm font-semibold text-gray-400">{currency}</span>
                  </div>
                </div>
                {selectedItem.billingCycle && (
                  <span className="text-xs font-bold text-[#FF4500] uppercase">
                    Billed {selectedItem.billingCycle}
                  </span>
                )}
              </div>

              <button 
                onClick={() => { addToCart(selectedItem, activeCatalog); setSelectedItem(null); }}
                className="w-full py-4 bg-[#FF4500] hover:bg-[#e03d00] text-white font-bold text-xs uppercase tracking-wider rounded-full shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
              >
                <span>Apply for Access</span>
                <ArrowRight size={16} />
              </button>
            </div>
          </div>

        </div>

      </div>
    </div>
  </div>
)}

     {/* CART MODAL */}
<div className={`fixed inset-0 z-[100] flex justify-end ${isCartOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
  {/* Dark Overlay with Blur */}
  <div
    className={`absolute inset-0 bg-gray-900/50 backdrop-blur-md transition-opacity duration-500 ${isCartOpen ? 'opacity-100' : 'opacity-0'}`}
    onClick={() => setIsCartOpen(false)}
  />

  {/* Main Modal Container */}
  <div
    className={`relative w-full max-w-full lg:max-w-6xl bg-[#f8f9fa] shadow-2xl h-full transform transition-all duration-500 ease-out flex flex-col overflow-y-auto lg:overflow-hidden ${isCartOpen ? 'translate-x-0' : 'translate-x-full'}`}
  >
    {/* Close Button */}
    <button 
      onClick={() => setIsCartOpen(false)} 
      className="absolute top-4 right-4 md:top-6 md:right-6 z-[110] w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white text-gray-700 hover:text-white hover:bg-[#FF4500] transition-all duration-300 shadow-md border border-gray-100"
    >
      <X size={20} strokeWidth={2.5} />
    </button>

    <div className="flex flex-col lg:flex-row lg:h-full lg:overflow-hidden">
      
      {/* Left Column: Cart Items List */}
      <div className="lg:flex-[1.3] lg:flex lg:flex-col bg-white lg:border-r border-gray-200/80 z-10">
        <div className="p-6 md:p-8 lg:p-10 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[#FF4500] font-bold text-xs uppercase tracking-wider">
              ✦ Service Portfolio ✦
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 tracking-tight">
            Selected Assets
          </h2>
        </div>

        <div className="lg:flex-1 lg:overflow-y-auto p-6 md:p-8 scrollbar-hide">
          {cart.length > 0 ? (
            <div className="space-y-4">
              {cart.map(cartItem => (
                <div 
                  key={cartItem.itemId} 
                  className="group relative flex items-center gap-4 p-4 bg-[#f8f9fa] rounded-2xl border border-gray-200/80 transition-all duration-300 hover:border-gray-300"
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-white border border-gray-100 flex items-center justify-center p-2 shadow-sm shrink-0 overflow-hidden">
                    <img 
                      src={getImageUrl(cartItem.image)} 
                      alt="" 
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" 
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold text-[#FF4500] uppercase tracking-wider mb-1">
                      {cartItem.category || 'Strategic Asset'}
                    </p>
                    <h4 className="text-sm md:text-base font-bold text-gray-900 truncate">
                      {cartItem.name}
                    </h4>
                    <button 
                      onClick={() => removeFromCart(cartItem.itemId)} 
                      className="text-[10px] text-red-500 hover:text-red-700 transition-colors mt-2 uppercase font-bold tracking-wider flex items-center gap-1"
                    >
                      <X size={10} /> Remove
                    </button>
                  </div>

                  <div className="flex flex-col items-end gap-3 shrink-0">
                    <div className="flex items-center bg-white rounded-full p-1 border border-gray-200 shadow-sm">
                      <button 
                        onClick={() => updateCartQuantity(cartItem.itemId, cartItem.quantity - 1)} 
                        className="p-1 md:p-1.5 text-gray-600 hover:text-[#FF4500] hover:bg-gray-50 rounded-full transition-colors"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="px-3 text-xs md:text-sm font-extrabold text-gray-900">
                        {cartItem.quantity}
                      </span>
                      <button 
                        onClick={() => updateCartQuantity(cartItem.itemId, cartItem.quantity + 1)} 
                        className="p-1 md:p-1.5 text-gray-600 hover:text-[#FF4500] hover:bg-gray-50 rounded-full transition-colors"
                      >
                        <Plus size={12} />
                      </button>
                    </div>

                    <p className="text-sm md:text-base font-extrabold text-gray-900 tracking-tight">
                      {(cartItem.price * cartItem.quantity).toLocaleString()} <span className="text-[10px] font-semibold text-gray-400">{currency}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center py-20 text-gray-300">
              <Briefcase size={56} strokeWidth={1.5} className="mb-4 text-gray-300" />
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Portfolio Empty</p>
            </div>
          )}
        </div>

        <div className="p-6 md:p-8 bg-white border-t border-gray-100">
          <button 
            onClick={() => setIsCartOpen(false)}
            className="flex items-center gap-2 text-gray-500 hover:text-[#FF4500] text-xs font-bold uppercase tracking-wider transition-colors group"
          >
            <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> 
            Keep exploring services
          </button>
        </div>
      </div>

      {/* Right Column: Checkout Protocol & Payment */}
      <div className="lg:flex-1 bg-[#f8f9fa] p-6 md:p-8 lg:p-10 lg:flex lg:flex-col lg:overflow-y-auto scrollbar-hide">
        <h3 className="text-xs font-extrabold text-gray-400 uppercase tracking-wider mb-6">
          Client Engagement Protocol
        </h3>

        <div className="flex-1 space-y-6">
          {/* Inputs Group */}
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase text-gray-400 ml-2 mb-1 block tracking-wider">
                Entity / Full Name
              </label>
              <input 
                placeholder="Ex: Global Solutions LDA" 
                value={client.name} 
                onChange={e => setClient({ ...client, name: e.target.value })} 
                className="w-full px-4 py-3.5 bg-white border border-gray-200/80 rounded-xl text-xs font-bold text-gray-900 focus:ring-2 focus:ring-[#FF4500]/20 focus:border-[#FF4500] outline-none transition-all shadow-sm" 
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input 
                placeholder="Email" 
                type="email" 
                value={client.email} 
                onChange={e => setClient({ ...client, email: e.target.value })} 
                className="w-full px-4 py-3.5 bg-white border border-gray-200/80 rounded-xl text-xs font-bold text-gray-900 focus:ring-2 focus:ring-[#FF4500]/20 focus:border-[#FF4500] outline-none transition-all shadow-sm" 
              />
              <input 
                type="number" 
                placeholder="Phone" 
                value={client.phone} 
                onChange={e => setClient({ ...client, phone: e.target.value })} 
                className="w-full px-4 py-3.5 bg-white border border-gray-200/80 rounded-xl text-xs font-bold text-gray-900 focus:ring-2 focus:ring-[#FF4500]/20 focus:border-[#FF4500] outline-none transition-all shadow-sm" 
              />
            </div>

            <textarea 
              placeholder="Specific case notes..." 
              rows={3} 
              value={client.notes || ""} 
              onChange={e => setClient({ ...client, notes: e.target.value })} 
              className="w-full px-4 py-3.5 bg-white border border-gray-200/80 rounded-xl text-xs font-bold text-gray-900 focus:ring-2 focus:ring-[#FF4500]/20 focus:border-[#FF4500] outline-none transition-all shadow-sm resize-none" 
            />
          </div>

          {/* Settlement Method Selector */}
          <div className="space-y-3">
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block ml-1">
              Settlement Method
            </label>
            <div className="grid grid-cols-2 gap-2">
              {['mpesa', 'visa', 'emola', 'transfer'].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setPaymentMethod(m as any); setMobileMoneyPhone(''); }}
                  className={`py-3.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2 border ${
                    paymentMethod === m 
                      ? 'bg-gray-900 text-white border-gray-900 shadow-md' 
                      : 'bg-white text-gray-700 border-gray-200/80 hover:bg-gray-50'
                  }`}
                >
                  {m === 'emola' ? 'e-Mola' : m === 'transfer' ? 'Bank Transfer' : m}
                  {paymentMethod === m && <Check size={12} className="text-[#FF4500]" strokeWidth={3} />}
                </button>
              ))}
            </div>

            {/* Phone Number Input for M-Pesa / E-Mola */}
            {(paymentMethod === 'mpesa' || paymentMethod === 'emola') && (
              <div className="flex flex-col gap-1.5 pt-1">
                <input
                  type="tel"
                  value={mobileMoneyPhone}
                  onChange={e => setMobileMoneyPhone(e.target.value)}
                  placeholder={paymentMethod === 'mpesa' ? '+258 84 XXX XXXX' : '+258 86 XXX XXXX'}
                  className="w-full px-4 py-3.5 bg-white border border-gray-200/80 rounded-xl text-xs font-bold text-gray-900 focus:ring-2 focus:ring-[#FF4500]/20 focus:border-[#FF4500] outline-none shadow-sm"
                />
                <p className="text-[10px] text-gray-400 font-medium ml-2">
                  {paymentMethod === 'mpesa'
                    ? 'O número deve começar com 84 ou 85'
                    : 'O número deve começar com 86 ou 87'}
                </p>
              </div>
            )}
          </div>

          {/* Bank Transfer Details Container */}
          {paymentMethod === 'transfer' && (
            <div className="p-5 bg-white border border-gray-200/80 text-gray-900 rounded-xl shadow-sm">
              <h4 className="text-xs font-extrabold uppercase tracking-wider mb-4 flex items-center gap-2 text-[#FF4500]">
                <CreditCard size={14} /> Settlement Accounts
              </h4>
              <div className="space-y-3">
                {company.bankAccounts?.map((bank, idx) => (
                  <div key={idx} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <p className="text-xs font-bold uppercase text-gray-900 mb-1">{bank.bankName}</p>
                    <div className="grid grid-cols-2 gap-2 text-[11px] font-mono text-gray-600">
                      <div>
                        <p className="text-[9px] uppercase text-gray-400">Account</p>
                        <p className="font-bold">{bank.accountNumber}</p>
                      </div>
                      <div>
                        <p className="text-[9px] uppercase text-gray-400">NIB/IBAN</p>
                        <p className="font-bold">{bank.nibOrIban}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Grand Total & Action Buttons */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex justify-between items-end mb-6">
            <div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                Grand Total
              </span>
              <div className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight">
                {totals.grandTotal.toLocaleString()} <span className="text-xs font-semibold text-gray-400">{currency}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {/* Online Gateway Payment Button */}
            <button
              onClick={handlePayment}
              disabled={!paymentMethod || !['mpesa', 'visa', 'emola'].includes(paymentMethod) || ((paymentMethod === 'mpesa' || paymentMethod === 'emola') && !mobileMoneyPhone)}
              className="w-full py-4 bg-[#FF4500] hover:bg-[#e03d00] text-white text-xs font-bold uppercase tracking-wider rounded-full transition-all shadow-md hover:shadow-lg disabled:opacity-30 disabled:hover:bg-[#FF4500] active:scale-95 flex items-center justify-center gap-2"
            >
              <Lock size={13} /> 
              {hasOrderWithPrice ? `Secure Payment (${totals.grandTotal.toLocaleString()})` : 'Initialize Gateway'}
            </button>

            {/* Manual / Transfer / Direct Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={submitting || !client.name || !client.email || (hasOrderWithPrice && ['mpesa', 'visa', 'emola'].includes(paymentMethod))}
              className="w-full py-4 bg-gray-900 hover:bg-gray-800 text-white text-xs font-bold uppercase tracking-wider rounded-full transition-all disabled:opacity-30 active:scale-95 shadow-sm"
            >
              {paymentMethod === 'transfer' ? 'Confirm Transfer Receipt' : 'Finalize Engagement Request'}
            </button>
          </div>
        </div>

      </div>

    </div>
  </div>
</div>

   {/* SUCCESS MODAL */}
{showSuccessModal && (
  <div 
    className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-6 bg-gray-900/50 backdrop-blur-md animate-in fade-in duration-300" 
    onClick={closeSuccessModal}
  >
    <div
      className="bg-white shadow-2xl max-w-sm md:max-w-md w-full relative overflow-hidden transition-all transform scale-100 rounded-3xl border border-gray-100"
      onClick={e => e.stopPropagation()}
    >
      {/* Top Accent Line */}
      <div className="absolute top-0 left-0 w-full h-1.5 bg-[#FF4500]" />

      <div className="p-8 md:p-10 text-center">
        {/* Animated Check Icon Badge */}
        <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-[#FF4500]/10 rounded-full mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-[#FF4500] rounded-full flex items-center justify-center text-white shadow-md shadow-[#FF4500]/30">
            <Check size={22} strokeWidth={3} />
          </div>
        </div>

        {/* Header & Message */}
        <div className="space-y-2 mb-8">
          <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#FF4500] block">
            Confirmed Engagement
          </span>

          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight tracking-tight">
            Order Processed
          </h2>

          <p className="text-gray-500 text-xs md:text-sm leading-relaxed max-w-[300px] mx-auto pt-1">
            Sua solicitação foi registrada. Um e-mail de confirmação foi enviado para o seu endereço profissional.
          </p>
        </div>

        {/* Action Button */}
        <button
          onClick={closeSuccessModal}
          className="w-full py-4 bg-[#FF4500] hover:bg-[#e03d00] text-white rounded-full font-bold text-xs uppercase tracking-wider shadow-md hover:shadow-lg transition-all active:scale-95 mb-6"
        >
          Return to Dashboard
        </button>

        {/* Reference Protocol Section */}
        <div className="pt-6 border-t border-dashed border-gray-200 flex flex-col items-center gap-1.5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
            Reference Protocol
          </p>
          <span className="font-mono text-xs md:text-sm font-extrabold text-gray-900 tracking-wider bg-[#f8f9fa] px-4 py-1.5 rounded-full border border-gray-200/80">
            #AX-{Math.random().toString(36).substr(2, 5).toUpperCase()}
          </span>
        </div>
      </div>
    </div>
  </div>
)}

    {/* FOOTER */}
<footer className="bg-gray-900 text-white pt-16 md:pt-24 pb-8 md:pb-12 overflow-hidden relative border-t border-gray-800">
  {/* Subtle Radial Grid Overlay */}
  <div 
    className="absolute inset-0 opacity-[0.03] pointer-events-none" 
    style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '32px 32px' }} 
  />

  <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 md:gap-16 mb-16 md:mb-24">
      
      {/* Brand & Newsletter Column */}
      <div className="sm:col-span-2 lg:col-span-4 space-y-6 md:space-y-8">
        <div className="flex items-center">
          {company.logo ? (
            <img src={company.logo} alt={company.name} className="h-8 md:h-10 w-auto brightness-0 invert" />
          ) : (
            <span className="font-extrabold text-xl md:text-2xl tracking-tight text-white uppercase">
              {company.name}<span className="text-[#FF4500]">.</span>
            </span>
          )}
        </div>

        <p className="text-gray-400 text-xs md:text-sm leading-relaxed max-w-xs font-normal">
          Líder global em inteligência estratégica e infraestrutura de alta performance. 
          Desenhando sistemas complexos com simplicidade absoluta.
        </p>

        {/* Newsletter Subscription */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#FF4500]" />
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-300">
              Strategic Intelligence
            </p>
          </div>
          <div className="flex p-1.5 bg-gray-800/80 border border-gray-700 rounded-full focus-within:border-[#FF4500] transition-all">
            <input 
              type="email" 
              placeholder="Corporate email access" 
              className="flex-1 px-4 md:px-5 bg-transparent text-white text-xs outline-none placeholder:text-gray-500 font-medium min-w-0" 
            />
            <button className="px-5 md:px-7 py-2.5 bg-[#FF4500] text-white rounded-full text-xs font-bold uppercase tracking-wider hover:bg-[#e03d00] transition-all shadow-md whitespace-nowrap">
              Subscribe
            </button>
          </div>
        </div>
      </div>

      {/* Operations Quick Links */}
      <div className="lg:col-span-2 space-y-6">
        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">
          Operations
        </h4>
        <ul className="space-y-3 md:space-y-4">
          {['Global Fleet', 'Tracking', 'Warehouse', 'Solutions'].map(link => (
            <li key={link}>
              <a 
                href="#" 
                className="text-xs text-gray-400 hover:text-[#FF4500] transition-colors font-medium uppercase tracking-wider flex items-center gap-2 group"
              >
                <span className="w-0 group-hover:w-3 h-0.5 bg-[#FF4500] transition-all" />
                {link}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Direct Contact */}
      <div className="lg:col-span-3 space-y-6">
        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">
          Direct Access
        </h4>
        <ul className="space-y-6">
          <li className="flex flex-col gap-1">
            <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
              Hotline 24/7
            </span>
            <a 
              href={`tel:${company.phone}`} 
              className="text-white font-extrabold text-xl md:text-2xl hover:text-[#FF4500] transition-colors tracking-tight"
            >
              {company.phone}
            </a>
          </li>
          <li className="flex flex-col gap-1">
            <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
              Official Liaison
            </span>
            <a 
              href={`mailto:${company.email}`} 
              className="text-gray-300 font-semibold text-xs md:text-sm hover:text-[#FF4500] transition-colors tracking-tight break-all"
            >
              {company.email}
            </a>
          </li>
        </ul>
      </div>

      {/* Social & Cleared Protocols */}
      <div className="lg:col-span-3 space-y-6">
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">
            Connect
          </h4>
          <div className="flex gap-3">
            {[Instagram, Facebook, Linkedin].map((Icon, idx) => (
              <a 
                key={idx} 
                href="#" 
                className="w-10 h-10 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#FF4500] hover:border-[#FF4500] transition-all duration-300 shadow-sm"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
            Cleared Protocols
          </p>
          <div className="flex flex-wrap gap-2">
            {['M-PESA', 'VISA', 'E-MOLA'].map(p => (
              <div 
                key={p} 
                className="px-3 py-1.5 border border-gray-800 bg-gray-800/40 rounded-lg text-[10px] font-bold text-gray-400 hover:text-white hover:border-gray-700 transition-all"
              >
                {p}
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>

    {/* Bottom Bar */}
    <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-6">
      
      {/* Copyright & Certifications */}
      <div className="space-y-2 text-center md:text-left">
        <p className="text-xs font-bold text-gray-300 uppercase tracking-wider">
          © 2026 {company.name} <span className="text-gray-600 font-normal">/ High-Stakes Infrastructure</span>
        </p>
        <div className="flex items-center justify-center md:justify-start gap-3 text-[10px] text-gray-500 font-bold uppercase tracking-wider">
          <span>ISO 9001 CERTIFIED</span>
          <span className="w-1 h-1 rounded-full bg-gray-700" />
          <span>GDPR COMPLIANT</span>
        </div>
      </div>

      {/* Privacy Links */}
      <div className="flex gap-6">
        <a href="#" className="text-xs font-bold text-gray-400 hover:text-[#FF4500] transition-colors uppercase tracking-wider">
          Privacy
        </a>
        <a href="#" className="text-xs font-bold text-gray-400 hover:text-[#FF4500] transition-colors uppercase tracking-wider">
          Terms
        </a>
      </div>

      {/* Live System Status Indicator */}
      <div className="flex items-center gap-2.5 px-4 py-2 bg-gray-800/60 rounded-full border border-gray-700/80">
        <div className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </div>
        <span className="text-[10px] font-bold text-gray-300 uppercase tracking-wider">
          System Status: Operational
        </span>
      </div>

    </div>
  </div>
</footer>
    
  {/* Awaiting Confirmation Modal */}
{showAwaitingConfirmation && (
  <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-6 bg-gray-900/50 backdrop-blur-md animate-in fade-in duration-300">
    <div className="bg-white shadow-2xl max-w-sm md:max-w-md w-full relative overflow-hidden transition-all transform scale-100 rounded-3xl border border-gray-100 p-8 md:p-10 text-center">
      
      {/* Top Accent Line */}
      <div className="absolute top-0 left-0 w-full h-1.5 bg-[#FF4500]" />

      {/* STATE 1: WAITING FOR PIN */}
      {pollStatus === 'waiting' && (
        <div className="space-y-6">
          <div className="relative w-16 h-16 md:w-20 md:h-20 mx-auto flex items-center justify-center">
            {/* Outer Soft Ring */}
            <div className="absolute inset-0 rounded-full border-4 border-[#FF4500]/20" />
            {/* Spinning Accent Ring */}
            <div className="absolute inset-0 rounded-full border-4 border-[#FF4500] border-t-transparent animate-spin" />
            <Smartphone className="w-7 h-7 text-[#FF4500] animate-pulse" />
          </div>

          <div className="space-y-2">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#FF4500] block">
              Authorization Required
            </span>
            <h3 className="text-xl md:text-2xl font-extrabold text-gray-900 tracking-tight">
              Aguardar confirmação
            </h3>
            <p className="text-xs md:text-sm text-gray-500 leading-relaxed max-w-[280px] mx-auto pt-1">
              Um pedido de pagamento foi enviado para o seu telemóvel.<br />
              Introduza o seu PIN no telefone para autorizar o pagamento.
            </p>
          </div>

          {awaitingRef && (
            <div className="pt-2">
              <span className="font-mono text-xs font-extrabold text-gray-900 tracking-wider bg-[#f8f9fa] px-4 py-1.5 rounded-full border border-gray-200/80 inline-block">
                Ref: {awaitingRef}
              </span>
            </div>
          )}
        </div>
      )}

      {/* STATE 2: PAYMENT CONFIRMED */}
      {pollStatus === 'confirmed' && (
        <div className="space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-emerald-50 rounded-full">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-emerald-500 rounded-full flex items-center justify-center text-white shadow-md shadow-emerald-500/30">
              <CheckCircle className="w-6 h-6" strokeWidth={2.5} />
            </div>
          </div>

          <div className="space-y-2">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-600 block">
              Transaction Approved
            </span>
            <h3 className="text-xl md:text-2xl font-extrabold text-gray-900 tracking-tight">
              Pagamento Confirmado!
            </h3>
            <p className="text-xs md:text-sm text-gray-400 font-medium pt-1">
              Redirecionando...
            </p>
          </div>
        </div>
      )}

      {/* STATE 3: PAYMENT FAILED / EXPIRED */}
      {pollStatus === 'failed' && (
        <div className="space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-rose-50 rounded-full">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-rose-500 rounded-full flex items-center justify-center text-white shadow-md shadow-rose-500/30">
              <X className="w-6 h-6" strokeWidth={3} />
            </div>
          </div>

          <div className="space-y-2">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-rose-600 block">
              Transaction Declined
            </span>
            <h3 className="text-xl md:text-2xl font-extrabold text-gray-900 tracking-tight">
              Pagamento não confirmado
            </h3>
            <p className="text-xs md:text-sm text-gray-500 leading-relaxed max-w-[280px] mx-auto pt-1">
              O pagamento foi recusado ou expirou.
            </p>
          </div>

          <button
            onClick={() => setShowAwaitingConfirmation(false)}
            className="w-full py-4 bg-gray-900 hover:bg-gray-800 text-white rounded-full font-bold text-xs uppercase tracking-wider transition-all shadow-md active:scale-95"
          >
            Tentar novamente
          </button>
        </div>
      )}

    </div>
  </div>
)}
</div>
  );
};

export default LogisticPortal;
